import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'

import { speakers } from '@/constants/metric'
import { easeLoad } from '@/lib/motion'
import { SectionHeading } from '@/components/generals/section-heading'
import { SpeakerCard } from '@/components/generals/speaker-card'
import { SwapLabel } from '@/components/generals/swap-label'

const INITIAL = 8

export const SpeakersSection: FC = (): ReactNode => {
    const [expanded, setExpanded] = useState(false)
    const extraRef = useRef<HTMLDivElement>(null)

    const visible = expanded ? speakers : speakers.slice(0, INITIAL)
    const hidden = speakers.length - INITIAL

    useEffect(() => {
        const host = extraRef.current

        if (!expanded || !host) return

        const items = host.querySelectorAll('[data-late-card]')

        if (!items.length) return

        const tween = gsap.fromTo(items, { opacity: 0.001 }, {
            opacity: 1,
            duration: 1.1,
            stagger: 0.1,
            ease: easeLoad
        })

        return () => {
            tween.kill()
        }
    }, [expanded])

    return (
        <section id='speakers' aria-labelledby='speakers-title' className='scroll-mt-[96px] px-0 py-20 tab:px-[60px] tab:py-28'>
            <div className='fw-container flex flex-col items-center gap-12 tab:gap-16'>
                <SectionHeading
                    tag='Speakers'
                    title={<span id='speakers-title'>Meet the speakers</span>}
                    description='The first eight names, with the rest of the programme announced through the spring.'
                />

                <div ref={extraRef} className='w-full'>
                    <ul className='grid w-full grid-cols-2 gap-5 desk:grid-cols-4'>
                        {visible.map((speaker, index) => (
                            <li
                                key={speaker.slug}
                                data-reveal={index < INITIAL ? 'fade' : undefined}
                                data-reveal-delay={index < INITIAL ? 0.1 * index : undefined}
                                data-late-card={index < INITIAL ? undefined : 'true'}
                            >
                                <SpeakerCard speaker={speaker} priority={index < 4} />
                            </li>
                        ))}
                    </ul>
                </div>

                {!expanded && hidden > 0 ? (
                    <button
                        type='button'
                        onClick={() => setExpanded(true)}
                        data-reveal='up'
                        className='fw-swap-host relative inline-flex h-[46px] items-center justify-center overflow-hidden border border-[#454545] px-5 font-sans text-[14px] font-medium uppercase leading-[1em] tracking-[0.02em] text-ink transition-colors duration-300 hover:border-[#fefff5] hover:bg-[#ffffff08]'
                    >
                        <SwapLabel>
                            Load more
                        </SwapLabel>
                    </button>
                ) : null}
            </div>
        </section>
    )
}
